import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Gift, Store } from 'lucide-react';
import { fadeInUp, staggerContainer, scaleIn, useParallax } from '../hooks/useScrollAnimation';
import SectionHeading from './common/SectionHeading';
import GlassPanel from './common/GlassPanel';

const cards = [
  {
    icon: CreditCard,
    title: 'White-Label Cards',
    description: 'Club-branded cards fans are proud to carry. Every transaction earns rewards and generates revenue.',
  },
  {
    icon: Gift,
    title: 'Smart Rewards',
    description: 'AI-powered offers personalized to each fan\'s preferences and spending habits.',
  },
  {
    icon: Store,
    title: 'Merchant Network',
    description: 'Growing network of partner merchants, online and offline, with closed-loop tracking.',
  },
];

const Product = () => {
  const { ref, y } = useParallax(40);

  return (
    <section id="product" className="relative py-24 md:py-32 bg-esf-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Fan Experience"
          headline="A platform fans actually use"
          subheadline="Everything your fans need in one beautiful, club-branded experience."
        />

        <div ref={ref} className="mt-16 flex justify-center">
          <motion.div
            variants={scaleIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            style={{ y }}
            className="relative flex items-end justify-center"
          >
            <picture>
              <source srcSet="/aik-iPhone-left.webp" type="image/webp" />
              <img
                src="/aik-iPhone-left.png"
                alt="The fan app rewards screen, showing points and available offers"
                width={337}
                height={551}
                loading="lazy"
                decoding="async"
                className="w-40 md:w-56 lg:w-64 h-auto animate-float relative z-10 -mr-4 md:-mr-6"
              />
            </picture>
            <picture>
              <source srcSet="/aik-iPhone-right.webp" type="image/webp" />
              <img
                src="/aik-iPhone-right.png"
                alt="The fan app home screen, showing the club-branded card and balance"
                width={327}
                height={564}
                loading="lazy"
                decoding="async"
                className="w-40 md:w-56 lg:w-64 h-auto animate-float-slow relative z-0 -ml-4 md:-ml-6"
              />
            </picture>
            <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[400px] h-[150px] bg-esf-blue/[0.08] rounded-full blur-3xl" />
          </motion.div>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {cards.map((card) => (
            <motion.div key={card.title} variants={fadeInUp}>
              <GlassPanel className="p-6 h-full">
                <card.icon size={24} className="text-esf-blue mb-4" aria-hidden="true" />
                <h3 className="text-body-lg font-semibold text-esf-white mb-2">{card.title}</h3>
                <p className="text-body-sm text-esf-gray-300">{card.description}</p>
              </GlassPanel>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Product;
